import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiEye, FiCamera, FiDroplet, FiSun } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Experiences = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const experiences = [
    {
      icon: FiEye,
      title: 'Endemic Birdwatching',
      description: 'Spot 34 endemic species including the Sri Lanka Blue Magpie and Red-faced Malkoha from our garden sanctuary',
      image: 'https://images.unsplash.com/photo-1444464666168-49d633b86797?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      highlights: ['Morning bird walks', 'Species identification guide', 'Photography tips']
    },
    {
      icon: FiDroplet,
      title: 'Waterfall Exploration',
      description: 'Access five pristine waterfalls including Kekuna Ella and Duvili Ella with safe swimming pools',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      highlights: ['Natural rock pools', 'Fish therapy streams', 'Guided safety tours']
    },
    {
      icon: FiCamera,
      title: 'Bridge Adventure',
      description: 'Cross the iconic suspension bridge at Pitadeniya entrance - your gateway to rainforest immersion',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      highlights: ['Instagram-worthy views', 'Adventure photography', 'Sunrise crossing']
    },
    {
      icon: FiSun,
      title: 'Natural River Bathing',
      description: 'Swim in the crystal-clear Gin Ganga river - a pure alternative to chlorinated pools',
      image: 'https://images.unsplash.com/photo-1439066615861-d1af74d74000?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      highlights: ['Pristine water quality', 'Natural mineral therapy', 'Peaceful meditation']
    }
  ];

  const trails = [
    { name: 'Kekuna Ella Trail', difficulty: 'Moderate', duration: '2-3 hours', distance: '3.2 km' },
    { name: 'Duvili Ella Circuit', difficulty: 'Easy', duration: '1-2 hours', distance: '2.1 km' },
    { name: 'Canopy Walk', difficulty: 'Easy', duration: '45 min', distance: '1.5 km' },
    { name: 'Endemic Species Trail', difficulty: 'Hard', duration: '4-5 hours', distance: '5.8 km' }
  ];

  return (
    <section id="experiences" className="py-20 relative overflow-hidden">
      <div className="container mx-auto px-6" ref={ref}>
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Playfair_Display']"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            Rainforest <span className="text-gradient">Experiences</span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            Immerse yourself in the authentic wonders of Mederipitiya's pristine ecosystem
          </motion.p>
        </motion.div>

        {/* Experience Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {experiences.map((experience, index) => (
            <motion.div
              key={experience.title}
              className="glass-card overflow-hidden hover-glow"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.6 + index * 0.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="md:flex">
                <div className="md:w-1/2 relative overflow-hidden">
                  <motion.img
                    src={experience.image}
                    alt={experience.title}
                    className="w-full h-64 md:h-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  />
                  <div className="absolute top-4 left-4">
                    <div className="w-12 h-12 glass-morphism rounded-full flex items-center justify-center">
                      <SafeIcon icon={experience.icon} className="w-6 h-6 text-blue-400" />
                    </div>
                  </div>
                </div>
                
                <div className="md:w-1/2 p-6">
                  <h3 className="text-xl font-semibold text-white mb-3 font-['Playfair_Display']">
                    {experience.title}
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed">
                    {experience.description}
                  </p>
                  <div className="space-y-2">
                    {experience.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <span className="text-gray-300 text-sm">{highlight}</span>
                      </div>
                    ))}
                  </div>
                  <motion.button
                    className="mt-4 px-4 py-2 glass-morphism text-white text-sm font-medium rounded-lg hover-glow"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Learn More
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trail Information */}
        <motion.div
          className="glass-card p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.4 }}
        >
          <h3 className="text-2xl font-bold text-white mb-6 font-['Playfair_Display'] text-center">
            Guided Trail Options
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trails.map((trail, index) => (
              <motion.div
                key={trail.name}
                className="glass-morphism p-4 rounded-lg hover-glow"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 1.6 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <h4 className="text-lg font-semibold text-white mb-2">{trail.name}</h4>
                <div className="space-y-1 text-sm text-gray-300">
                  <div className="flex justify-between">
                    <span>Difficulty:</span>
                    <span className={`font-medium ${
                      trail.difficulty === 'Easy' ? 'text-green-400' :
                      trail.difficulty === 'Moderate' ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {trail.difficulty}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span>{trail.duration}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Distance:</span>
                    <span>{trail.distance}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Safety & Equipment */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.8 }}
        >
          <div className="glass-card p-6 max-w-4xl mx-auto">
            <h3 className="text-xl font-bold text-white mb-4 font-['Playfair_Display']">
              Safety First: Leech Protection & Equipment
            </h3>
            <p className="text-gray-300 mb-4">
              We provide complimentary leech socks, Dettol spray, and safety briefings for all guests. 
              Our experienced guides ensure your rainforest adventure is both thrilling and secure.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
              <span className="glass-morphism px-3 py-1 rounded-full">Free Leech Socks</span>
              <span className="glass-morphism px-3 py-1 rounded-full">Safety Briefings</span>
              <span className="glass-morphism px-3 py-1 rounded-full">First Aid Kit</span>
              <span className="glass-morphism px-3 py-1 rounded-full">Emergency Contact</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experiences;